import { IDateTime } from '..'
import { ConditionTypeEnum } from '.'

export interface IDateCondition {
  /**
   * The values needed to evaluate the condition
   */
  comparison: {
    /**
     * Set true if the comparison should consider both the date and time.  This field only applies to the EXACT_DATE and CUSTOM_DATE_FIELD modifiers
     */
    compareDateAndTime?: boolean
    /**
     * Set true if the comparison value is another field (e.g. eta is after appointment time)
     */
    comparisonIsAnotherField: boolean
    /**
     * The expected condition value
     *
     * @example
     *
     * operator: 'IS_WITHIN', modifier: 'NUMBER_OF_DAYS_FROM_NOW', expectedValue: '5' // number
     *
     * operator: 'IS', modifier: 'EXACT_DATE', expectedValue: {date: 2021-01-01', time: '15:00', timezone: 'America/New_York'} //IDateTime
     *
     * operator: 'IS', modifier: 'CUSTOM_DATE_FIELD', expectedValue: 'eta' // string
     */
    expectedValue?: string | number | IDateTime

    /**
     * The modifier to operator, e.g. (today, tomorrow, yesterday, the number of days from now, the number of days ago)
     */
    modifier?: IsBeforeAfterNotModifiersEnum | ModifiersForIsWithinDateOperatorEnum

    /**
     * The operator to evaluate the condition, e.g. (is, is not, is before, is after, is on or before, is on or after, is within)
     */
    operator: DateOperatorEnum
  }
  /**
   * The field name associated with the condition, e.g. ('eta')
   */
  conditionName: string
  /**
   * The type of condition
   */
  conditionType: ConditionTypeEnum.Date
}

/**
 * The operator values for the date condition enum
 */
export enum DateOperatorEnum {
  /**
   * Requires ModifiersForIsDateOperator
   */
  IS = 'is',
  /**
   * Requires ModifiersForIsAfterDateOperator
   */
  IS_AFTER = 'is after',
  /**
   * Requires ModifiersForIsBeforeDateOperator
   */
  IS_BEFORE = 'is before',
  /**
   * No modifiers
   */
  IS_EMPTY = 'is empty',
  /**
   * Requires ModifiersForIsNotDateOperator
   */
  IS_NOT = 'is not',
  /**
   * No modifiers
   */
  IS_NOT_EMPTY = 'is not empty',
  /**
   * Requires ModifiersForIsOnOrAfterDateOperator
   */
  IS_ON_OR_AFTER = 'is on or after',
  /**
   * Requires ModifiersForIsOnOrBeforeDateOperator
   */
  IS_ON_OR_BEFORE = 'is on or before',
  /**
   * Requires ModifiersForIsWithinDateOperator
   */
  IS_WITHIN = 'is within'
}

export enum IsBeforeAfterNotModifiersEnum {
  CUSTOM_DATE_FIELD = 'custom date field',
  EXACT_DATE = 'exact date',
  NUMBER_OF_DAYS_AGO = 'number of days ago',
  NUMBER_OF_DAYS_FROM_NOW = 'number of days from now',
  ONE_MONTH_AGO = 'one month ago',
  ONE_MONTH_FROM_NOW = 'one month from now',
  ONE_WEEK_AGO = 'one week ago',
  ONE_WEEK_FROM_NOW = 'one week from now',
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  YESTERDAY = 'yesterday'
}

export enum ModifiersForIsWithinDateOperatorEnum {
  THE_NEXT_MONTH = 'the next month',
  THE_NEXT_NUMBER_OF_DAYS = 'the next number of days',
  THE_NEXT_WEEK = 'the next week',
  THE_NEXT_YEAR = 'the next year',
  THE_PAST_MONTH = 'the past month',
  THE_PAST_NUMBER_OF_DAYS = 'the past number of days',
  THE_PAST_WEEK = 'the past week',
  THE_PAST_YEAR = 'the past year'
}
